import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "@/components/landing/Wordmark";
import SiteFooter from "@/components/landing/SiteFooter";
import { SUPPORT_EMAIL } from "@/lib/contact";

/**
 * Real counsel-approved legal pages for /privacy, /terms, /refunds.
 * Each route renders its own text verbatim. The CONTACT address is
 * interpolated from the shared SUPPORT_EMAIL constant — not hardcoded
 * anywhere else — so the address is changeable in exactly one place
 * (/app/frontend/src/lib/contact.js).
 */

const SITE_ORIGIN = "https://voxiumscript.com";
const EFFECTIVE_DATE = "Effective October 2026";
const COMPANY_LINE = "RayMar & Co Enterprises LLC";

const PRIVACY = {
  title: "PRIVACY POLICY",
  intro:
    'Voxium Script is operated by RayMar & Co Enterprises LLC ("we", "us"). This policy explains what we collect, what we do with it, and how long we keep it.',
  sections: [
    {
      heading: "WHAT WE COLLECT",
      paragraphs: [
        "Account information: your email address, and the name you choose to display. You sign in with a one-tap link sent to your email. You can also choose to set a password as a second way in. If you do, we store it so we can check it when you sign in, and you can remove it at any time from your account settings.",
        "Payment information: handled by Stripe. We never see or store your card number. We keep a record of what you bought, when, and for how much.",
        "Your screenplays and the material you upload: described in its own section below, because it is the part that matters most.",
        "Usage information: which features you use and when, so we can keep the service working and understand what to improve.",
      ],
    },
    {
      heading: "YOUR SCREENPLAY",
      paragraphs: [
        "When you upload a file, we read the text out of it and then discard the file itself. We do not keep your PDF, your Final Draft file, or whatever else you sent us.",
        "If you are signed in, the extracted text is saved alongside the coverage we generate, so you can reopen your work later. You can delete any script and its coverage at any time from inside the app, and when you do, it is removed.",
        "We do not train AI models on your work, and we do not license, sell, or share your screenplays with anyone for their own use.",
      ],
    },
    {
      heading: "HOW YOUR WORK IS PROCESSED",
      paragraphs: [
        "Generating coverage requires sending your script text to AI model providers. We do this through our platform vendor, which routes requests to large language model providers under a contractual obligation not to train on customer content. We cannot independently verify which provider handles any individual request, and we would rather tell you that than imply a certainty we do not have.",
        "Your script text is also stored in our database and in our hosting provider's infrastructure in order to serve it back to you.",
      ],
    },
    {
      heading: "ANALYTICS AND COOKIES",
      // vox_ref paragraph added 2026-10-21 per Alain's referral-capture
      // brief. FLAGGED FOR COUNSEL: whether this requires an explicit
      // consent gate under Quebec Law 25 is a legal call, not made here.
      paragraphs: [
        "Our marketing site at voxiumscript.com uses analytics supplied by our hosting vendor. This includes session recording, which captures page content and interactions on that site. Our application at work.voxiumscript.com — the place you upload and read screenplays — does not run session recording. Both sites set an identifier cookie that lasts one year.",
        "If you arrive on our marketing site through a partner or affiliate link, we set a cookie named vox_ref so we can credit the partner who referred you. It stores only the referral code from that link — nothing that identifies you personally — and it lasts 90 days.",
        "We do not currently offer an in-product consent control for this. If you would prefer we exclude you, write to us at the address at the end of this policy and we will do it.",
      ],
    },
    {
      heading: "HOW LONG WE KEEP THINGS",
      paragraphs: [
        "Scripts and coverage you delete yourself: removed when you delete them.",
        "Your account, if you delete it: your scripts, coverage, mentor conversations, audio files and account record. We begin deletion immediately and complete it within 60 days. Backups rotate on their own schedule, which is why we say 60 days rather than instantly.",
        "What deleting your account does not remove: the record of what you bought, kept for seven years as described below; a short log that an account was deleted, which holds your account ID, the date, and the IP address the request came from; usage counts, meaning how much of the service was used and what it cost us, which contain none of your text; and the customer record Stripe holds for payments, which Stripe keeps under its own policies. Text-to-speech audio we have generated is cached without your account details and is not removed when you delete your account.",
        "Billing records and records of your consent to recurring charges: seven years. We are required to keep financial records, and consent records are what let us answer a question about a charge years later. These contain the transaction, not your screenplay.",
        "Server logs: 90 days.",
      ],
    },
    {
      heading: "YOUR RIGHTS",
      paragraphs: [
        "You can see your data, correct it, download your coverage, or delete your account from inside the app. If you would rather we did any of that for you, write to us.",
        "If you are in California, you have rights under the CCPA to know what we collect, to delete it, and to not be discriminated against for asking. We do not sell personal information and we do not share it for cross-context behavioral advertising.",
      ],
    },
    {
      heading: "SECURITY",
      paragraphs: [
        "We use encrypted connections and access controls. No service can promise perfect security, and we are not going to pretend otherwise. If we discover a breach affecting your data, we will tell you.",
      ],
    },
    {
      heading: "CHILDREN",
      paragraphs: [
        "Voxium Script is not for anyone under 18 and we do not knowingly collect information from children.",
      ],
    },
    {
      heading: "CHANGES",
      paragraphs: [
        "If we change this policy in a way that materially affects you, we will email you at least 30 days before it takes effect, and the effective date at the top of this page will change.",
      ],
    },
  ],
};

const REFUNDS = {
  title: "REFUND POLICY",
  intro:
    "Voxium Script is operated by RayMar & Co Enterprises LLC. This page covers cancellation, renewals, and what happens to credit you have not used.",
  sections: [
    {
      heading: "SUBSCRIPTIONS",
      paragraphs: [
        "Your plan renews automatically at the price shown on the pricing page until you cancel.",
        "You can cancel at any time, yourself, from Account then Billing. You do not need to contact us and we will not ask you why.",
        "When you cancel, you keep everything you have already paid for. Your access and any credits granted for the current billing period remain available until the end of that period. At the end of it, the subscription lapses and you are not charged again.",
        "We do not prorate and we do not refund part of a billing period you have already started. If you cancel on day three of a month you paid for, you keep the remaining twenty-seven days rather than getting money back for them.",
      ],
    },
    {
      heading: "ONE-TIME PURCHASES",
      paragraphs: [
        "This covers per-script coverage and Q&A credit packs.",
        "If you have used less than 20% of what you bought, write to us and we will refund it in full.",
        "If you have used 20% or more, it is not refundable. At that point you have had the substance of what you paid for.",
        "When we refund a one-time purchase, any credit remaining from that purchase is removed from your account. We do not take back credit you already spent, and we will not drive your balance below zero.",
      ],
    },
    {
      heading: "CHARGEBACKS",
      paragraphs: [
        "If you dispute a charge with your bank, we treat it the same way we treat a refund: unspent credit from that purchase is removed. We would much rather you wrote to us first. A dispute takes weeks and an email takes a day.",
      ],
    },
    {
      heading: "DELETING YOUR ACCOUNT",
      paragraphs: [
        "Deleting your account cancels your subscription immediately and any paid time left in the current period is forfeited rather than refunded. If you would rather use the time you have already paid for, cancel instead of deleting, and delete at the end of the period.",
      ],
    },
    {
      heading: "HOW TO ASK",
      paragraphs: [
        `Write to ${SUPPORT_EMAIL} with the email address on your account and what you bought. We will tell you where you stand against the rules above.`,
      ],
    },
  ],
};

const TERMS = {
  title: "TERMS OF SERVICE",
  intro:
    'These terms are an agreement between you and RayMar & Co Enterprises LLC ("we", "us"), the company that operates Voxium Script. By creating an account or buying anything from us, you agree to them.',
  sections: [
    {
      heading: "WHAT VOXIUM SCRIPT IS",
      paragraphs: [
        "Voxium Script reads screenplays and produces written coverage and development notes using AI models. You upload a script, we generate a read, you get it back.",
      ],
    },
    {
      heading: "WHAT IT IS NOT, STATED PLAINLY",
      paragraphs: [
        "It is not a human industry read, and we do not claim it replaces one.",
        "It does not predict whether your screenplay will sell, get made, place in a competition, or earn money. We make no representation about commercial outcomes of any kind.",
        "It does not give you access to anyone. We are not an agency, a manager, a production company, or a submission service, and buying coverage does not put your script in front of any person in the industry.",
        "It is not legal, financial, or professional advice.",
        "The coverage is one machine-generated opinion, produced by models that can be wrong, inconsistent, or confidently mistaken. Treat it as a reaction to your work, not a verdict on it.",
      ],
    },
    {
      heading: "YOUR ACCOUNT",
      paragraphs: [
        "You must be 18 or older. You are responsible for what happens under your account and for keeping access to the email address you sign in with, since we send sign-in links to it.",
      ],
    },
    {
      heading: "YOUR WORK",
      paragraphs: [
        "You own your screenplays. We claim no ownership of anything you upload and we acquire no rights in your work.",
        "You grant us only the permission we need to run the service for you: to process your script in order to generate your coverage, to store it so you can reopen it, and to display it back to you. That permission ends when you delete the material or your account.",
        "We do not train AI models on your work. We do not sell it, license it, or share it with anyone for their own purposes. How your script is processed and how long we keep it is set out in our Privacy Policy.",
        "You are responsible for having the right to upload what you upload.",
      ],
    },
    {
      heading: "ACCEPTABLE USE",
      paragraphs: [
        "Do not upload material you do not have the rights to. Do not use the service to produce anything unlawful. Do not attempt to break, overload, scrape, or reverse-engineer the service, resell access to it, or use it to build a competing product.",
        "We can suspend or close an account that does these things.",
      ],
    },
    {
      heading: "PAYING FOR IT",
      paragraphs: [
        "Prices are shown on our pricing page before you buy.",
        "Subscriptions renew automatically at the stated price and interval until you cancel. You consent to that recurring charge at checkout, separately from the purchase button itself. You can cancel at any time, yourself, from Account then Billing, without contacting us.",
        "Cancellation, proration and refunds are governed by our Refund Policy, which forms part of these terms.",
        "We may change prices for future billing periods. If we do, we will tell you before the change takes effect and you can cancel before it does. We will not change the price of a period you have already paid for.",
      ],
    },
    {
      heading: "AVAILABILITY",
      paragraphs: [
        "We try to keep the service running and we do not promise it always will be. Features may change. We may discontinue the service, and if we do, we will give you notice and a way to export your work.",
      ],
    },
    {
      heading: "DISCLAIMERS",
      paragraphs: [
        "The service is provided as it is. To the fullest extent the law allows, we disclaim implied warranties of merchantability, fitness for a particular purpose, and non-infringement.",
      ],
    },
    {
      heading: "LIMITATION OF LIABILITY",
      paragraphs: [
        "To the fullest extent the law allows, neither party is liable to the other for indirect, incidental, special, consequential, or punitive damages, or for lost profits or lost opportunities.",
        "Our total liability to you for any claim relating to the service is limited to the amount you paid us in the twelve months before the claim arose.",
        "Nothing in these terms limits liability that cannot be limited by law.",
      ],
    },
    {
      heading: "ENDING IT",
      paragraphs: [
        "You can stop using the service and delete your account at any time. Deleting your account cancels your subscription immediately and forfeits any paid time remaining in the current period, which is why the Refund Policy suggests cancelling instead and deleting at period end.",
        "We may suspend or close your account for a serious or repeated breach of these terms. If we do it for any other reason, we will refund the unused portion of what you have paid.",
      ],
    },
    {
      heading: "DISPUTES",
      paragraphs: [
        "Please read this section carefully. It affects how disputes between us are resolved, and it includes a waiver of your right to a jury trial and to participate in a class action.",
        `Talk to us first. Before starting an arbitration, you agree to tell us what the problem is in writing at ${SUPPORT_EMAIL}, and we agree to do the same for you. Neither of us will start an arbitration for 60 days after that notice, so that we have a real chance to settle it. The notice should describe the dispute and what you want. Any limitation period is paused during those 60 days.`,
        "Agreement to arbitrate. If we cannot resolve it, you and we agree that any dispute arising out of or relating to these terms or the service will be resolved by binding individual arbitration rather than in court. This covers claims that arose before you accepted these terms and claims that arise after you stop using the service.",
        "Who runs it, and under what rules. Arbitration is administered by the American Arbitration Association under its Consumer Arbitration Rules, by a single arbitrator. The Federal Arbitration Act governs this section.",
        "Who pays. We pay all arbitration filing, administrative and arbitrator fees beyond the first $200, which is yours. If the arbitrator finds your claim was frivolous or brought for an improper purpose, the AAA rules on fee allocation apply instead. We will not seek our attorneys' fees from you unless the arbitrator finds your claim frivolous.",
        "Where it happens. If your claim is for $25,000 or less, you may choose to have it decided on documents alone, by telephone, or by videoconference. If a hearing in person is held, it will be in the county where you live or somewhere else we both agree. You will never have to travel to us.",
        "Small claims. Either of us may bring an individual claim in small claims court instead, if it qualifies. Filing there is not a waiver of this section for anything else.",
        "Intellectual property and misuse. Either of us may ask a court for an injunction or other equitable relief to stop infringement or misuse of intellectual property, or unauthorised access to the service, without first going to arbitration.",
        "Public injunctive relief. Nothing in this section prevents you from seeking public injunctive relief, and a claim for public injunctive relief may be brought in court. If a court decides that a claim for public injunctive relief may not be arbitrated, that claim is severed and stays in court while the rest of the dispute proceeds in arbitration.",
        "No class actions. You and we each agree to bring claims only in an individual capacity, and not as a plaintiff or class member in any class, collective, consolidated or representative proceeding. The arbitrator may not consolidate more than one person's claims or preside over any form of representative proceeding. If this paragraph is found unenforceable as to a particular claim or request for relief, then this entire DISPUTES section does not apply to that claim or request for relief, and it proceeds in court — the rest of this section still applies to everything else.",
        "Many similar claims at once. If 25 or more similar arbitration demands are filed against us by or with the help of the same lawyers or coordinated group, we and you agree the AAA will administer them in batches of no more than 50, with one arbitrator per batch and one set of fees per batch, and that the limitation period is paused for the claims awaiting their batch.",
        `Your right to opt out. You can decline this entire DISPUTES section. Email ${SUPPORT_EMAIL} within 30 days of first accepting these terms, with your name and the email address on your account, and say that you opt out of arbitration. That is all it takes. Opting out does not affect anything else in these terms and we will not treat you differently for it.`,
        "The arbitrator's decision. The arbitrator decides the dispute, including whether a claim is covered by this section, except that a court decides whether the class-action paragraph above is enforceable. The award is final and binding and may be entered as a judgment in any court with jurisdiction.",
        "If we change this section. If we change this DISPUTES section after you accept these terms, the change does not apply to any dispute where we had already received notice.",
      ],
    },
    {
      heading: "GOVERNING LAW",
      paragraphs: [
        "These terms are governed by the laws of the State of California, without regard to its conflict-of-laws rules.",
      ],
    },
    {
      heading: "CHANGES TO THESE TERMS",
      paragraphs: [
        "If we change these terms in a way that materially affects you, we will email you at least 30 days before the change takes effect, and the effective date at the top of this page will change. Continuing to use the service after that means you accept the new terms.",
      ],
    },
    {
      heading: "THE WHOLE AGREEMENT",
      paragraphs: [
        "These terms, together with the Privacy Policy and the Refund Policy, are the entire agreement between us about the service. If any part is found unenforceable, the rest still applies.",
      ],
    },
  ],
};

const PolicyPage = ({ docTitle, slug, policy, testId }) => {
  useEffect(() => {
    const prevTitle = document.title;
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;

    document.title = `${docTitle} — Voxium Script`;
    if (canonical) canonical.setAttribute("href", `${SITE_ORIGIN}/${slug}`);

    return () => {
      document.title = prevTitle;
      if (canonical && prevCanonical !== null) {
        canonical.setAttribute("href", prevCanonical);
      }
    };
  }, [docTitle, slug]);

  return (
    <main
      className="min-h-screen bg-cream flex flex-col"
      data-testid={testId}
    >
      <header className="w-full px-6 sm:px-10 lg:px-16 pt-8 pb-4 border-b border-cream-200">
        <div className="max-w-[78rem] mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center tap-extend"
            aria-label="Voxium Script home"
            data-testid={`${testId}-home-link`}
          >
            <Wordmark tone="light" size="nav" testId={`${testId}-wordmark`} />
          </Link>
        </div>
      </header>

      <section className="flex-1 w-full px-6 sm:px-10 lg:px-16 py-16 sm:py-24">
        <article className="max-w-prose3 mx-auto">
          <h1
            className="display-serif text-nearblack text-4xl sm:text-5xl leading-[1.05] mb-3 tracking-tight"
            data-testid={`${testId}-title`}
          >
            {policy.title}
          </h1>
          <p
            className="font-ui text-sm text-nearblack/60 uppercase tracking-widest mb-14"
            data-testid={`${testId}-effective`}
          >
            {EFFECTIVE_DATE}
          </p>

          <p
            className="font-body text-base sm:text-[17px] text-nearblack/85 leading-relaxed mb-12"
            data-testid={`${testId}-intro`}
          >
            {policy.intro}
          </p>

          {policy.sections.map((section) => (
            <section
              key={section.heading}
              className="mb-12"
              data-testid={`${testId}-section-${section.heading
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "")}`}
            >
              <h2 className="display-serif text-nearblack text-xl sm:text-2xl mb-5 tracking-tight">
                {section.heading}
              </h2>
              {section.paragraphs.map((p, idx) => (
                <p
                  key={idx}
                  className="font-body text-base sm:text-[17px] text-nearblack/85 leading-relaxed mb-4 last:mb-0"
                >
                  {p}
                </p>
              ))}
            </section>
          ))}

          <section className="mb-12" data-testid={`${testId}-section-contact`}>
            <h2 className="display-serif text-nearblack text-xl sm:text-2xl mb-5 tracking-tight">
              CONTACT
            </h2>
            <p className="font-body text-base sm:text-[17px] text-nearblack/85 leading-relaxed mb-2">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="underline decoration-gold/60 underline-offset-2 hover:text-gold-600"
                data-testid={`${testId}-contact-link`}
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p className="font-body text-base sm:text-[17px] text-nearblack/85 leading-relaxed">
              {COMPANY_LINE}
            </p>
          </section>

          <p className="font-body text-nearblack/60 text-sm mt-16">
            <Link
              to="/"
              className="underline decoration-nearblack/30 underline-offset-4 hover:text-gold-600 hover:decoration-gold"
              data-testid={`${testId}-back-link`}
            >
              ← Back to voxiumscript.com
            </Link>
          </p>
        </article>
      </section>

      <SiteFooter />
    </main>
  );
};

export const PrivacyPage = () => (
  <PolicyPage
    docTitle="Privacy Policy"
    slug="privacy"
    policy={PRIVACY}
    testId="privacy-page"
  />
);

export const TermsPage = () => (
  <PolicyPage
    docTitle="Terms of Service"
    slug="terms"
    policy={TERMS}
    testId="terms-page"
  />
);

export const RefundsPage = () => (
  <PolicyPage
    docTitle="Refunds"
    slug="refunds"
    policy={REFUNDS}
    testId="refunds-page"
  />
);
